
// M&S Travel CR Content Bible — authoritative source for all site content.
// The journey consumes this data as a cinematic presentation layer.

export const CONTACT = {
  brand: 'M&S Travel',
  brandFull: 'M&S Travel CR: Tours y Transporte en Costa Rica',
  tagline: 'M&S Travel tu transporte de confianza',
  whatsappNumber: '50660953758',
  phoneDisplay: '+506 6095 3758',
  email: 'infomystravel@gmail.com',
  address: 'Alajuela, La Fortuna, Costa Rica',
  instagram: 'https://instagram.com/mystravelcr',
  facebook: 'https://facebook.com/mystravelcr',
  website: 'https://www.mystravelcr.com',
}

export function whatsappLink(message: string): string {
  return `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(message)}`
}

export const IMAGES = {
  brandLogo: 'https://cdn.magicpatterns.com/uploads/4GCcdAr16CGqnLYhcjqsoh/Screenshot_2026-07-20_at_1.07.29_PM.png',
  watermarkLogo: 'https://cdn.magicpatterns.com/uploads/ozBAKqZdbWTdqU37bgTvo9/Diseno_sin_titulo.png',
  landingPlane: 'https://cdn.magicpatterns.com/patterns/generated-images/39ebde65-c974-488b-8936-9d31ed9e655a.jpg',
  airportPickup: 'https://cdn.magicpatterns.com/patterns/generated-images/4f6885ed-4af4-41d1-864d-6bd66a96e184.jpg',
  privateTransport: 'https://cdn.magicpatterns.com/patterns/generated-images/f10e2584-4930-454b-baa6-ace489e0a0f8.jpg',
  privateRoutes: 'https://cdn.magicpatterns.com/patterns/generated-images/5d727103-a386-41f8-aa2b-fffb91d741d4.jpg',
  closingSunset: 'https://cdn.magicpatterns.com/patterns/generated-images/a900d622-c5b1-4bd9-8100-abb6eda19e7a.jpg',
  personalized: 'https://cdn.magicpatterns.com/patterns/generated-images/754f6263-7411-4804-9fea-8525b6459f5e.jpg',
  mistico: 'https://cdn.magicpatterns.com/patterns/generated-images/9ba97876-7983-4903-b6e7-8326262b7aef.jpg',
  chocolate: 'https://cdn.magicpatterns.com/patterns/generated-images/80f92342-fcab-46b9-832c-86c1afe211be.jpg',
  atv: 'https://cdn.magicpatterns.com/patterns/generated-images/129b77c9-1d72-456b-88a8-6c2c5c29cd44.jpg',
  sloth: 'https://cdn.magicpatterns.com/patterns/generated-images/85f5437f-0330-4d3b-a42b-92341bf3a1fe.jpg',
  rafting: 'https://cdn.magicpatterns.com/patterns/generated-images/d12ef545-5a82-4b18-8973-e548ec335a95.jpg',
  mundoAventura: 'https://cdn.magicpatterns.com/patterns/generated-images/789099b1-db06-4aa5-892a-33484e65910b.jpg',
  waveCombos: 'https://cdn.magicpatterns.com/patterns/generated-images/bdf37b05-37b8-4744-832c-85a5a3e81a79.jpg',
  canyoning: 'https://cdn.magicpatterns.com/patterns/generated-images/fdd0df6e-0497-443a-b303-d79e3151582a.jpg',
  arenalNight: 'https://cdn.magicpatterns.com/patterns/generated-images/81655db5-8218-4353-8569-6c4f833c24a9.jpg',
}

export type Atmosphere = 'sky' | 'terminal' | 'road' | 'rainforest' | 'calm' | 'dusk' | 'sunset'

export type JourneyChapter = {
  id: string
  mapLabel: string
  eyebrow: string
  title: string
  lead: string
  details: string[]
  highlights?: string[]
  image: string
  imageAlt: string
  atmosphere: Atmosphere
  align?: 'left' | 'center'
  ctaLabel?: string
  ctaMessage?: string
}

export const TRANSPORT_REGIONS = [
  'Conexiones aéreas y ciudades centrales',
  'Playas y costas de Guanacaste · Pacífico Norte',
  'Destinos naturales y rurales claves',
  'Playas y costas · Pacífico Central',
  'Caribe y región atlántica',
]

export const WAVE_COMBOS = [
  'Rafting y ATV',
  'Rafting y canyoning',
  'Rafting y chocolate',
  'Rafting y puentes colgantes',
  'Rafting y tour de perezosos',
  'Rafting y canopy',
  'Safari con tortilla',
  'Rafting II–III y tubing',
  'Rafting III–IV y tubing',
]

export const JOURNEY_CHAPTERS: JourneyChapter[] = [
  {
    id: 'arrival',
    mapLabel: 'Llegada',
    eyebrow: 'M&S Travel CR · Tours y Transporte en Costa Rica',
    title: 'Aterrizaste. Ahora respirá.',
    lead: 'Tu aventura empieza desde que llegás. Nosotros nos encargamos del camino.',
    details: ['M&S Travel CR: Tours y Transporte en Costa Rica.', 'M&S Travel tu transporte de confianza.'],
    image: IMAGES.landingPlane,
    imageAlt: 'Avión llegando a Costa Rica sobre la costa tropical',
    atmosphere: 'sky',
    align: 'center',
    ctaLabel: 'Empezar el viaje',
  },
  {
    id: 'private-transfer',
    mapLabel: 'Traslado',
    eyebrow: 'Transporte privado',
    title: 'Ya estamos aquí. Todo está listo.',
    lead: 'Reserva tu traslado privado y seguro por Costa Rica: cómodo, puntual y diseñado para tu ruta.',
    details: [
      'Garantiza un viaje seguro, cómodo y puntual por Costa Rica.',
      'Llena los detalles de tu ruta y recibe una cotización personalizada y el enlace de reserva en tu correo en menos de 2 horas, durante horario de oficina.',
      'Nuestro servicio de transporte privado te acompaña de manera cómoda, segura y tranquila.',
    ],
    highlights: ['Traslado privado', 'Seguro', 'Cómodo', 'Puntual'],
    image: IMAGES.airportPickup,
    imageAlt: 'Conductor privado esperando a viajeros junto a un vehículo',
    atmosphere: 'terminal',
    ctaLabel: 'Cotizar mi traslado',
    ctaMessage: 'Hola, quiero cotizar un traslado privado en Costa Rica.',
  },
  {
    id: 'routes',
    mapLabel: 'Rutas',
    eyebrow: 'Costa Rica, de costa a costa',
    title: 'Tu ruta no tiene límites.',
    lead: 'Conectamos tu llegada con los paisajes, pueblos y experiencias que querés vivir.',
    details: ['M&S Travel ofrece traslado privado y seguro por Costa Rica.', 'Explora rutas entre aeropuertos, ciudades centrales, costas, regiones rurales y el Caribe.'],
    highlights: TRANSPORT_REGIONS,
    image: IMAGES.privateRoutes,
    imageAlt: 'Vehículo privado recorriendo una carretera entre paisajes costarricenses',
    atmosphere: 'road',
    align: 'center',
    ctaLabel: 'Planear mi ruta',
    ctaMessage: 'Hola, quiero planear una ruta de transporte privado por Costa Rica.',
  },
  {
    id: 'our-story',
    mapLabel: 'Nuestra historia',
    eyebrow: 'Pero primero · déjanos presentarnos',
    title: 'No se trata solo de llegar. Se trata de sentir el camino acompañado.',
    lead: 'Cuando bajás del avión, M&S Travel ya está pensando en tu recorrido. Nacimos en el corazón de La Fortuna de San Carlos, inspirados por el Volcán Arenal y por la pasión de conectar personas con los tesoros de Costa Rica.',
    details: [
      'Tras más de 18 años de experiencia en el sector, nuestro fundador Elfrin Morales entendió que lo más importante no es solo llegar, sino disfrutar plenamente el camino.',
      'Por eso transformamos cada traslado y tour en una experiencia inolvidable.',
      'Nuestro propósito diario es ser tus socios de viaje en Costa Rica, con tours y transporte seguros, puntuales y cómodos.',
      'Nos enfocamos en superar las expectativas a través de la personalización y la conexión humana, para ser la empresa de referencia en La Fortuna y asegurar que cada viajero se lleve recuerdos inolvidables del país.',
    ],
    image: IMAGES.personalized,
    imageAlt: 'Viajeros compartiendo un momento frente a un paisaje de Costa Rica',
    atmosphere: 'calm',
    align: 'center',
  },
  {
    id: 'explore',
    mapLabel: 'Explorar',
    eyebrow: 'Explora con nosotros',
    title: 'La aventura se siente mejor compartida.',
    lead: 'Te ofrecemos una variedad extensa de actividades llenas de pura diversión, aventura y adrenalina para disfrutar en pareja, con amigos o en familia.',
    details: ['Cada experiencia puede comenzar con el transporte privado de M&S Travel: vos elegís el ritmo, nosotros cuidamos el recorrido.'],
    highlights: ['Parejas', 'Amigos', 'Familias', 'Aventura', 'Naturaleza'],
    image: IMAGES.privateTransport,
    imageAlt: 'Carretera entre selva y montañas de Costa Rica',
    atmosphere: 'road',
    align: 'center',
  },
  {
    id: 'sibaeli',
    mapLabel: 'Sibaeli',
    eyebrow: 'Tour de chocolate Sibaeli',
    title: 'El cacao guarda una historia.',
    lead: 'Sumérgete en el delicioso mundo del chocolate: conoce su historia, el proceso de elaboración y degusta sabores únicos llenos de aroma, sabor y tradición.',
    details: [
      'La familia Sibaeli es una empresa familiar dedicada a la producción de productos de cacao gourmet, totalmente saludables.',
      'Han diseñado un recorrido para vivir al máximo los diferentes procesos de elaboración de su delicioso chocolate artesanal.',
      'Al final podrás degustar bebidas de chocolate tradicionales y ancestrales que deleitarán tu paladar, además de descubrir sus deliciosos y diferentes productos.',
      'Con M&S Travel, nuestro servicio de transporte privado te lleva a vivir el auténtico y delicioso Tour de Chocolate Sibaeli.',
    ],
    highlights: ['Familia Sibaeli', 'Chocolate artesanal', 'Bebidas tradicionales y ancestrales', 'Productos de cacao gourmet'],
    image: IMAGES.chocolate,
    imageAlt: 'Cacao y chocolate artesanal del Tour de Chocolate Sibaeli',
    atmosphere: 'calm',
    ctaLabel: 'Consultar Tour Sibaeli',
    ctaMessage: 'Hola, quiero consultar el Tour de Chocolate Sibaeli y el transporte privado.',
  },
  {
    id: 'mistico',
    mapLabel: 'Mistico',
    eyebrow: 'Mistico Park · Puentes colgantes',
    title: 'El bosque no termina donde empieza el cielo.',
    lead: 'Disfruta vistas majestuosas y la emoción de los puentes colgantes en un ecosistema de alta biodiversidad vegetal y animal.',
    details: [
      'Mistico Park es un lugar mágico rodeado de naturaleza y animales, con una experiencia llena de adrenalina en sus puentes colgantes.',
      'Es un paraíso para amantes de la observación de aves: desde coloridos tucanes hasta diminutos colibríes que llenan el bosque de vida y movimiento.',
      'En el tour nocturno podrás adentrarte en el bosque y ver cómo la naturaleza cobra vida: ranas, insectos y reptiles en el vibrante ambiente nocturno de Mistico Park.',
      'M&S Travel te lleva a vivir esta increíble experiencia. Contáctanos y cotiza tu servicio de transporte privado.',
    ],
    highlights: ['Puentes colgantes', 'Observación de aves', 'Tour nocturno', 'Flora y fauna'],
    image: IMAGES.mistico,
    imageAlt: 'Puente colgante en el bosque nuboso de Mistico Park',
    atmosphere: 'rainforest',
    ctaLabel: 'Descubrir Mistico',
    ctaMessage: 'Hola, quiero consultar Mistico Park y transporte privado.',
  },
  {
    id: 'mundo-aventura',
    mapLabel: 'Mundo Aventura',
    eyebrow: 'Arenal Mundo Aventura',
    title: 'El Arenal se vive desde adentro.',
    lead: 'Un parque ecológico para experimentar adrenalina, naturaleza y cultura frente al majestuoso Volcán Arenal.',
    details: [
      'Disfruta expediciones en la naturaleza, experiencias culturales, canopy, cabalgatas y caminatas nocturnas.',
      'Recorre senderos a caballo, disfruta vistas impresionantes del Volcán Arenal y explora la selva de una manera auténtica.',
      'Atraviesa el bosque tropical en canopy con increíbles vistas del volcán y la naturaleza que rodea La Fortuna.',
      'En las caminatas nocturnas presencia la vibrante fauna y flora; atrévete también al rappel descendiendo paredes impresionantes rodeadas de naturaleza.',
      'Reserva tu transporte privado con M&S Travel: te llevamos de la forma más cómoda y segura. Cotiza tu viaje sin ningún compromiso.',
    ],
    highlights: ['Cabalgata', 'Canopy', 'Tour nocturno', 'Rappel', 'Experiencias culturales'],
    image: IMAGES.mundoAventura,
    imageAlt: 'Cabalgata con el Volcán Arenal sobre la selva',
    atmosphere: 'rainforest',
    ctaLabel: 'Vivir Mundo Aventura',
    ctaMessage: 'Hola, quiero cotizar Arenal Mundo Aventura y transporte privado.',
  },
  {
    id: 'atv',
    mapLabel: 'ATV',
    eyebrow: 'Arenal Adventure ATV',
    title: 'Barro, ríos y pura aventura.',
    lead: 'Aventura sobre ruedas por paisajes increíbles, rodeado de naturaleza y diversión todoterreno.',
    details: [
      'El equipo de Arenal Adventure ATV te lleva por senderos, ríos, barro y pura aventura, cuidando tu seguridad para que cada tour sea inolvidable.',
      'La compañía entiende que la calidad del equipo es crucial: invierte y mantiene una flota de modernos vehículos todo terreno para garantizar disfrute, seguridad y comodidad.',
      'Consulta horarios y prepárate para recorrer caminos llenos de adrenalina y diversión cerca del majestuoso Volcán Arenal.',
      'Con M&S Travel, tu transporte privado de confianza, llegás de manera cómoda y segura. Relájate, disfruta el camino y prepárate para la aventura.',
    ],
    highlights: ['Tours en ATV', 'Senderos', 'Ríos', 'Barro', 'Flota moderna'],
    image: IMAGES.atv,
    imageAlt: 'ATV recorriendo un sendero de lodo en la selva de Arenal',
    atmosphere: 'dusk',
    ctaLabel: 'Quiero vivir ATV',
    ctaMessage: 'Hola, quiero consultar Arenal Adventure ATV y transporte privado.',
  },
  {
    id: 'sloths',
    mapLabel: "Sloth's Territory",
    eyebrow: "Sloth's Territory",
    title: 'El bosque se mueve despacio.',
    lead: 'Un paraíso natural de 13 hectáreas, con un ecosistema vibrante para mirar con calma.',
    details: [
      'Visita Sloth’s Territory con M&S Travel: podrás realizar observación de aves y mirar de cerca perezosos de 2 y 3 dedos en su hábitat natural, además de la fauna y flora de la zona.',
      'Admira aves como tucanes y colibríes mientras caminas entre senderos rodeados de naturaleza con guías expertos.',
      'Consulta el horario de tour diurno, horario de tour nocturno y las consideraciones para este tour.',
      'En M&S Travel buscamos garantizar experiencias inolvidables: reserva y nosotros nos encargamos de llevarte de la manera más cómoda, segura y tranquila. Contáctanos y cotiza sin compromiso.',
    ],
    highlights: ['13 hectáreas', 'Perezosos de 2 y 3 dedos', 'Observación de aves', 'Tucanes y colibríes', 'Tours diurno y nocturno'],
    image: IMAGES.sloth,
    imageAlt: 'Perezoso en su hábitat natural en Sloth’s Territory',
    atmosphere: 'rainforest',
    ctaLabel: 'Visitar Sloth’s Territory',
    ctaMessage: 'Hola, quiero consultar Sloth’s Territory y transporte privado.',
  },
  {
    id: 'wave',
    mapLabel: 'Wave',
    eyebrow: 'Wave Expeditions',
    title: 'Elegí tu propia mezcla de adrenalina.',
    lead: 'Wave Expeditions ofrece actividades llenas de adrenalina para hacer de tu viaje toda una aventura.',
    details: [
      'Descubre los diferentes combos de tours de Wave Expeditions. Escríbenos para consultar detalles e información relevante sobre cada actividad.',
      'M&S Travel puede coordinar tu traslado privado para que vivas el combo que mejor se adapte a tu aventura.',
    ],
    highlights: WAVE_COMBOS,
    image: IMAGES.waveCombos,
    imageAlt: 'Rafting y aventura en la selva de Costa Rica',
    atmosphere: 'dusk',
    align: 'center',
    ctaLabel: 'Consultar combos Wave',
    ctaMessage: 'Hola, quiero más detalles sobre los combos de Wave Expeditions.',
  },
  {
    id: 'arenal-rafting',
    mapLabel: 'Rafting',
    eyebrow: 'Arenal Rafting',
    title: 'El río no espera.',
    lead: 'En el corazón de La Fortuna, navega rápidos emocionantes rodeado de selva tropical y paisajes espectaculares.',
    details: [
      'Si buscás adrenalina y naturaleza en un solo lugar, Arenal Rafting es el plan perfecto: rafting en ríos llenos de acción y descenso de cañones en el corazón de La Fortuna.',
      'Sentí la energía, naturaleza y diversión de conquistar el río: una experiencia inolvidable durante tu visita a Costa Rica.',
      'Viví también una aventura única de descenso de cañones: superá impresionantes obstáculos, explorá la selva y disfrutá una experiencia cargada de adrenalina en La Fortuna.',
      'M&S Travel te lleva de manera cómoda y segura para que solo tengas que prepararte para la aventura.',
    ],
    highlights: ['Rafting', 'Canyoning', 'Rápidos', 'Selva tropical', 'La Fortuna'],
    image: IMAGES.canyoning,
    imageAlt: 'Canyoning junto a una cascada de la selva de La Fortuna',
    atmosphere: 'dusk',
    ctaLabel: 'Quiero esta aventura',
    ctaMessage: 'Hola, quiero consultar Arenal Rafting, descenso de cañones y transporte privado.',
  },
  {
    id: 'contact',
    mapLabel: 'Contactar',
    eyebrow: 'Contáctanos',
    title: 'Tu próxima historia empieza con un mensaje.',
    lead: 'Si tienes una duda, consulta o necesitás más información sobre nuestros servicios, en M&S Travel estaremos encantados de ayudarte para que tu experiencia sea perfecta.',
    details: [
      `Correo electrónico: ${CONTACT.email}.`,
      `Número telefónico: ${CONTACT.phoneDisplay}.`,
      `Dirección: ${CONTACT.address}.`,
      'Reserva tu traslado privado ahora y recibe una cotización personalizada y enlace de reserva por correo en menos de 2 horas, durante horario de oficina.',
    ],
    highlights: ['Tours', 'Transporte privado', 'Cotización personalizada', 'Atención en La Fortuna'],
    image: IMAGES.closingSunset,
    imageAlt: 'Atardecer sobre el Volcán Arenal en Costa Rica',
    atmosphere: 'sunset',
    align: 'center',
    ctaLabel: 'Hablar con M&S Travel',
    ctaMessage: 'Hola M&S Travel, quiero recibir información y una cotización personalizada.',
  },
  {
    id: 'feedback',
    mapLabel: 'Tu opinión',
    eyebrow: 'Servicio al cliente',
    title: 'Cada viaje nos enseña algo.',
    lead: 'En M&S Travel trabajamos cada día para ofrecer transporte privado de la más alta calidad. Tu opinión es muy importante para nosotros.',
    details: [
      'Te invitamos a ayudarnos completando nuestra encuesta de servicio al cliente.',
      'Tus comentarios nos permiten crecer, mejorar y seguir brindando experiencias cada vez más cómodas y seguras para todos nuestros viajeros.',
      'M&S Travel tu transporte de confianza.',
      `Dirección: ${CONTACT.address}.`,
    ],
    image: IMAGES.arenalNight,
    imageAlt: 'Rana de ojos rojos en el bosque nocturno de Costa Rica',
    atmosphere: 'dusk',
    align: 'center',
    ctaLabel: 'Consultar encuesta',
    ctaMessage: 'Hola M&S Travel, quiero completar la encuesta de servicio al cliente.',
  },
]

export const NAV_ITEMS = [
  { id: 'inicio', label: 'Inicio', scene: 0 },
  { id: 'transporte', label: 'Transporte', scene: 1 },
  { id: 'experiencias', label: 'Experiencias', scene: 5 },
  { id: 'nosotros', label: 'Nosotros', scene: 3 },
  { id: 'contacto', label: 'Contacto', scene: 13 },
]

export type TravelGuideItem = {
  id: string
  label: string
  eyebrow: string
  title: string
  intro: string
  tips: Array<{ title: string; detail: string }>
}

export const TRAVEL_GUIDE: TravelGuideItem[] = [
  {
    id: 'arrival',
    label: 'Al aterrizar',
    eyebrow: 'Llegar sin complicaciones',
    title: 'De la puerta de llegada a tu primer respiro.',
    intro: 'El Aeropuerto Internacional Juan Santamaría (SJO) es el punto de llegada habitual para quienes exploran La Fortuna, el Pacífico y Guanacaste.',
    tips: [
      { title: 'Encontrá a tu conductor', detail: 'Confirmá los detalles de tu recogida antes de salir. M&S Travel coordina una bienvenida privada, puerta a puerta, para tu ruta.' },
      { title: 'Mantenete conectado', detail: 'El Wi-Fi del aeropuerto sirve para enviar tu primer mensaje a casa. Una eSIM o SIM local da más flexibilidad una vez en ruta.' },
      { title: 'Llegá a tu propio ritmo', detail: 'Las carreteras de Costa Rica invitan a no apresurarse. Considerá tiempo para tránsito, clima, miradores y una primera parada.' },
    ],
  },
  {
    id: 'getting-around',
    label: 'Moverte por Costa Rica',
    eyebrow: 'Confianza en el camino',
    title: 'La distancia también es parte de la aventura.',
    intro: 'Costa Rica parece compacta en el mapa, pero las montañas, la lluvia y las paradas escénicas cambian el ritmo de un día. El transporte privado se adapta a tu viaje.',
    tips: [
      { title: 'Planeá según el ritmo del viaje', detail: 'Elegí una región principal a la vez, en lugar de correr de costa a costa. Verás más si te movés menos.' },
      { title: 'Dejá que alguien más maneje', detail: 'Un conductor privado elimina el estrés de navegación, parqueo y traslados largos, sobre todo después de un vuelo internacional.' },
      { title: 'Mantené días flexibles', detail: 'El clima y las condiciones de la vía pueden cambiar. M&S Travel ayuda a diseñar una ruta cómoda, sin prisas.' },
    ],
  },
  {
    id: 'before-you-go',
    label: 'Antes de salir',
    eyebrow: 'Empacá con confianza',
    title: 'Empacá ligero. Preparate para todo.',
    intro: 'Costa Rica puede pasar de sol cálido a lluvia tropical en un mismo día, especialmente cerca del Arenal y la selva.',
    tips: [
      { title: 'El clima cambia rápido', detail: 'Protección ligera contra la lluvia, ropa fresca, botella reutilizable y zapatos cerrados cómodos cubren la mayoría de los días.' },
      { title: 'Tarjetas y efectivo', detail: 'Las tarjetas se aceptan en muchos hoteles, restaurantes y operadores. Llevá algunos colones para compras pequeñas y propinas.' },
      { title: 'Protegé tus esenciales', detail: 'Usá bloqueador solar, repelente y una funda impermeable para el celular o la cámara en aventuras húmedas.' },
    ],
  },
  {
    id: 'peace-of-mind',
    label: 'Viajá con tranquilidad',
    eyebrow: 'Acompañamiento local',
    title: 'Un plan tranquilo hace un viaje mejor.',
    intro: 'Costa Rica es acogedora y está preparada para recibir viajeros. Como en cualquier lugar nuevo, una pequeña preparación te deja enfocarte en la experiencia.',
    tips: [
      { title: 'Elegí aliados de confianza', detail: 'Elegí transporte confirmado y experiencias consolidadas. Hacer preguntas antes de reservar y recibir respuestas claras es parte de un buen servicio.' },
      { title: 'Llevá cerca tus objetos de valor', detail: 'Usá solo lo necesario en las excursiones y guardá pasaporte y efectivo extra en la caja de seguridad del hotel cuando esté disponible.' },
      { title: 'Tené a alguien a quien escribir', detail: 'Guardá a M&S Travel en WhatsApp antes de aterrizar. Un contacto local real vuelve sencillo cualquier cambio de planes.' },
    ],
  },
]

export const ROUTE_OPTIONS = [
  { id: 'la-fortuna', region: 'La Fortuna y Arenal', driveTime: 'aprox. 3 horas', note: 'Paisajes volcánicos, aguas termales, selva y el corazón de M&S Travel.' },
  { id: 'guanacaste', region: 'Guanacaste', driveTime: 'aprox. 4–5 horas', note: 'Playas del Pacífico, bosque tropical seco y un ritmo más lento junto al mar.' },
  { id: 'pacific', region: 'Pacífico Central', driveTime: 'aprox. 2.5–3.5 horas', note: 'Acceso sencillo a playas, vida silvestre y atardeceres frente al océano.' },
  { id: 'caribbean', region: 'Caribe', driveTime: 'aprox. 4–5 horas', note: 'Una costa distinta, cultura vibrante y otra manera de vivir Costa Rica.' },
]

export type ActivityProfile = {
  duration: string
  difficulty: string
  idealFor: string
}

export const ACTIVITY_PROFILES: Record<string, ActivityProfile> = {
  sibaeli: { duration: 'Ritmo de medio día', difficulty: 'Fácil y sensorial', idealFor: 'Amantes de la gastronomía y familias' },
  mistico: { duration: 'Medio día o día completo', difficulty: 'Fácil a moderado', idealFor: 'Amantes de la naturaleza y fotógrafos' },
  'mundo-aventura': { duration: 'Vos elegís el ritmo', difficulty: 'Aventura flexible', idealFor: 'Viajeros activos y grupos' },
  atv: { duration: 'Adrenalina de medio día', difficulty: 'Alta energía', idealFor: 'Buscadores de aventura' },
  sloths: { duration: 'Lento y contemplativo', difficulty: 'Caminata fácil', idealFor: 'Familias y amantes de la fauna' },
  wave: { duration: 'Armá tu propio combo', difficulty: 'Elegí tu nivel', idealFor: 'Amigos y buscadores de adrenalina' },
  'arenal-rafting': { duration: 'Día de aventura', difficulty: 'Activo', idealFor: 'Amantes del río y viajeros atrevidos' },
}

export type Language = 'es' | 'en'

export type LocalizedContent = {
  language: Language
  htmlLang: string
  tagline: string
  navigation: {
    homeLabel: string
    menuOpenLabel: string
    menuCloseLabel: string
    mapLabel: string
    companionLabel: string
    switchLabel: string
    switchPath: string
    switchAriaLabel: string
  }
  journey: {
    startLabel: string
    detailsLabel: string
    hideDetailsLabel: string
    highlightsLabel: string
    progressLabel: string
    scrollHint: string
  }
  concierge: {
    triggerEyebrow: string
    triggerLabel: string
    eyebrow: string
    title: string
    intro: string
    closeLabel: string
    askLabel: string
    whatsappMessage: string
  }
  planner: {
    eyebrow: string
    title: string
    regionsLabel: string
    airportLabel: string
    driveLabel: string
    disclaimer: string
  }
  activity: {
    ariaLabel: string
    paceLabel: string
    feelLabel: string
    bestForLabel: string
    careLabel: string
    careText: string
  }
  whatsapp: {
    ariaLabel: string
    message: string
  }
  navItems: Array<{ id: string; label: string; scene: number }>
  chapters: JourneyChapter[]
  travelGuide: TravelGuideItem[]
  routeOptions: Array<{ id: string; region: string; driveTime: string; note: string }>
  activityProfiles: Record<string, ActivityProfile>
}

export const ES_CONTENT: LocalizedContent = {
  language: 'es',
  htmlLang: 'es',
  tagline: CONTACT.tagline,
  navigation: {
    homeLabel: 'M&S Travel — volver al inicio',
    menuOpenLabel: 'Abrir mapa del viaje',
    menuCloseLabel: 'Cerrar mapa del viaje',
    mapLabel: 'Mapa del viaje',
    companionLabel: 'Hablar con tu compañero de viaje',
    switchLabel: 'EN',
    switchPath: '/EN',
    switchAriaLabel: 'Ver la versión en inglés',
  },
  journey: {
    startLabel: 'Empezar el viaje',
    detailsLabel: 'Ver información de la experiencia',
    hideDetailsLabel: 'Ocultar información',
    highlightsLabel: 'Puntos destacados de la experiencia',
    progressLabel: 'Progreso del viaje',
    scrollHint: 'Deslizá dentro de este cuadro para ver más',
  },
  concierge: {
    triggerEyebrow: 'Tu compañero de viaje',
    triggerLabel: 'Planeá mi viaje a Costa Rica',
    eyebrow: 'Concierge M&S Travel',
    title: 'Tu viaje ya está en buenas manos.',
    intro: 'Usá esta guía práctica antes de salir, al aterrizar y cada vez que necesités una respuesta local.',
    closeLabel: 'Cerrar compañero de viaje',
    askLabel: 'Consultar a M&S Travel',
    whatsappMessage: 'Hola M&S Travel, tengo una consulta sobre mi viaje a Costa Rica.',
  },
  planner: {
    eyebrow: 'Planificador de ruta privada',
    title: '¿A dónde querés ir primero?',
    regionsLabel: 'Regiones de Costa Rica',
    airportLabel: 'Aeropuerto Juan Santamaría',
    driveLabel: 'Traslado estimado',
    disclaimer: 'Los tiempos son estimados y pueden variar por clima, condiciones de la vía y paradas. M&S Travel confirma la mejor ruta para tu llegada.',
  },
  activity: {
    ariaLabel: 'Información de la experiencia',
    paceLabel: 'Ritmo',
    feelLabel: 'Nivel',
    bestForLabel: 'Ideal para',
    careLabel: 'Acompañamiento M&S:',
    careText: 'coordinamos el transporte privado y el horario ideal alrededor de esta experiencia.',
  },
  whatsapp: {
    ariaLabel: 'Escribir a M&S Travel por WhatsApp',
    message: 'Hola M&S Travel, necesito ayuda para planear mi viaje a Costa Rica.',
  },
  navItems: NAV_ITEMS,
  chapters: JOURNEY_CHAPTERS,
  travelGuide: TRAVEL_GUIDE,
  routeOptions: ROUTE_OPTIONS,
  activityProfiles: ACTIVITY_PROFILES,
}

